import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Filter, Search, Baby, ChevronLeft, Edit, X, Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SchoolRegisterView = ({ onBack }) => {
  const [data, setData] = useState({ children: [], schools: [], terms: [] });
  const [loading, setLoading] = useState(true);
  const [selectedSchool, setSelectedSchool] = useState('all');
  const [selectedTerm, setSelectedTerm] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Child editing state
  const [editingChild, setEditingChild] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '', surname: '', year_group: 1, school_class: '',
    emergency_contact: '', medical_conditions: ''
  });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/school-register`);
      setData(response.data);
      // Auto-select first term if available (most recent)
      if (response.data.terms?.length > 0) {
        setSelectedTerm(response.data.terms[0]);
      }
    } catch (error) { 
      console.error('Failed to fetch register:', error); 
      toast.error('Failed to load school register');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const startEditChild = (child) => {
    setEditingChild(child);
    setEditForm({
      name: child.name || '',
      surname: child.surname || '',
      year_group: child.year_group || 1,
      school_class: child.school_class || '',
      emergency_contact: child.emergency_contact || '',
      medical_conditions: child.medical_conditions || ''
    });
  };

  const cancelEdit = () => {
    setEditingChild(null);
    setEditForm({ name: '', surname: '', year_group: 1, school_class: '', emergency_contact: '', medical_conditions: '' });
  };

  const saveChildEdit = async () => {
    if (!editingChild) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/children/${editingChild.id}`, editForm);
      toast.success(`${editForm.name}'s profile updated`);
      fetchData(); // Refresh data
      cancelEdit();
    } catch (error) {
      console.error('Failed to update child:', error);
      toast.error(error.response?.data?.detail || 'Failed to update child profile');
    } finally {
      setSaving(false);
    }
  };

  const filteredChildren = data.children.filter(child => {
    // School filter
    if (selectedSchool !== 'all' && child.school_id !== selectedSchool) return false;
    // Term filter
    if (selectedTerm !== 'all' && child.term !== selectedTerm) return false;
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      if (!child.name.toLowerCase().includes(term) && 
          !child.parent_name?.toLowerCase().includes(term)) return false;
    }
    return true;
  });

  // Sort by year group then name
  const sortedChildren = [...filteredChildren].sort((a, b) => {
    if (a.year_group !== b.year_group) return a.year_group - b.year_group;
    return a.name.localeCompare(b.name);
  });

  const handleDownload = () => {
    // Generate CSV with just child name and year group
    const schoolName = selectedSchool === 'all' 
      ? 'All_Schools' 
      : data.schools.find(s => s.id === selectedSchool)?.name || 'School';
    const termName = selectedTerm === 'all' ? 'All_Terms' : selectedTerm;
    
    const csvContent = [
      ['Child Name', 'Year Group'],
      ...sortedChildren.map(c => {
        // Combine name and surname, sanitize for CSV
        const fullName = c.surname ? `${c.name} ${c.surname}` : c.name;
        return [fullName, `Year ${c.year_group}`];
      })
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const fileName = `school_register_${schoolName.replace(/\s+/g, '_')}_${termName.replace(/\s+/g, '_')}.csv`;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    toast.success('Register downloaded');
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="back-to-dashboard">
            <ChevronLeft className="w-5 h-5 text-slate-600" />
          </button>
          <h1 className="font-heading text-3xl font-bold text-slate-900">School Register</h1>
        </div>
        <button 
          onClick={handleDownload} 
          disabled={sortedChildren.length === 0}
          className="btn-primary flex items-center gap-2"
          data-testid="download-register"
        >
          <Download className="w-5 h-5" /> Download Register
        </button>
      </div>

      {/* Filters */}
      <div className="card-plain">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-slate-500" />
          <span className="font-heading font-bold text-slate-900">Filter Register</span>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block font-body text-sm font-semibold text-slate-700 mb-1">Term</label>
            <select 
              value={selectedTerm} 
              onChange={(e) => setSelectedTerm(e.target.value)} 
              className="w-full px-4 py-2 border border-slate-200 rounded-xl font-body focus:ring-2 focus:ring-teal-500"
              data-testid="term-filter"
            >
              <option value="all">All Terms</option>
              {data.terms?.map(term => (
                <option key={term} value={term}>{term}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block font-body text-sm font-semibold text-slate-700 mb-1">School</label>
            <select 
              value={selectedSchool} 
              onChange={(e) => setSelectedSchool(e.target.value)} 
              className="w-full px-4 py-2 border border-slate-200 rounded-xl font-body focus:ring-2 focus:ring-teal-500"
              data-testid="school-filter"
            >
              <option value="all">All Schools</option>
              {data.schools?.map(school => (
                <option key={school.id} value={school.id}>{school.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block font-body text-sm font-semibold text-slate-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl font-body focus:ring-2 focus:ring-teal-500"
                data-testid="search-register"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Register Table */}
      <div className="card-plain">
        <div className="flex items-center justify-between mb-4">
          <span className="font-body text-slate-600">{sortedChildren.length} children</span>
          {(selectedTerm !== 'all' || selectedSchool !== 'all') && (
            <button 
              onClick={() => { setSelectedTerm('all'); setSelectedSchool('all'); setSearchTerm(''); }}
              className="text-sm text-teal-600 hover:text-teal-700 font-body font-semibold"
            >
              Clear filters
            </button>
          )}
        </div>

        {sortedChildren.length === 0 ? (
          <div className="text-center py-12">
            <Baby className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="font-body text-slate-600">No children found for the selected filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="register-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 font-heading font-bold text-slate-900">Child Name</th>
                  <th className="text-left py-3 px-4 font-heading font-bold text-slate-900">Year Group</th>
                  <th className="text-left py-3 px-4 font-heading font-bold text-slate-900">Parent Name</th>
                  <th className="text-left py-3 px-4 font-heading font-bold text-slate-900">Parent Phone</th>
                  <th className="text-left py-3 px-4 font-heading font-bold text-slate-900">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedChildren.map((child, idx) => (
                  <tr key={`${child.id}-${child.enrollment_id || idx}`} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`register-row-${child.id}`}>
                    <td className="py-3 px-4 font-body font-semibold text-slate-900">
                      {child.name} {child.surname || ''}
                    </td>
                    <td className="py-3 px-4 font-body text-slate-700">Year {child.year_group}</td>
                    <td className="py-3 px-4 font-body text-slate-700">{child.parent_name || '-'}</td>
                    <td className="py-3 px-4 font-body text-slate-700">{child.parent_phone || '-'}</td>
                    <td className="py-3 px-4">
                      <button 
                        onClick={() => startEditChild(child)}
                        className="p-2 hover:bg-teal-50 rounded-lg transition-colors"
                        title="Edit child"
                        data-testid={`edit-child-${child.id}`}
                      >
                        <Edit className="w-4 h-4 text-teal-600" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Child Modal */}
      {editingChild && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl border-2 border-slate-900 shadow-sticker max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-2xl font-bold text-slate-900">Edit Child Profile</h2>
                <button onClick={cancelEdit} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5 text-slate-600" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block font-body font-semibold text-slate-700 mb-1">First Name *</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-200 rounded-xl font-body focus:ring-2 focus:ring-teal-500"
                    required
                    data-testid="edit-child-name"
                  />
                </div>
                
                <div>
                  <label className="block font-body font-semibold text-slate-700 mb-1">Surname</label>
                  <input
                    type="text"
                    value={editForm.surname}
                    onChange={(e) => setEditForm({...editForm, surname: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-200 rounded-xl font-body focus:ring-2 focus:ring-teal-500"
                    data-testid="edit-child-surname"
                  />
                </div>

                <div>
                  <label className="block font-body font-semibold text-slate-700 mb-1">Year Group *</label>
                  <select
                    value={editForm.year_group}
                    onChange={(e) => setEditForm({...editForm, year_group: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 border border-slate-200 rounded-xl font-body focus:ring-2 focus:ring-teal-500"
                    data-testid="edit-child-year"
                  >
                    {[1,2,3,4,5,6].map(y => <option key={y} value={y}>Year {y}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block font-body font-semibold text-slate-700 mb-1">School Class</label>
                  <input
                    type="text"
                    value={editForm.school_class}
                    onChange={(e) => setEditForm({...editForm, school_class: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-200 rounded-xl font-body focus:ring-2 focus:ring-teal-500"
                    placeholder="e.g., 3B, Year 4 Blue"
                    data-testid="edit-child-class"
                  />
                </div>

                <div>
                  <label className="block font-body font-semibold text-slate-700 mb-1">Emergency Contact</label>
                  <input
                    type="text"
                    value={editForm.emergency_contact}
                    onChange={(e) => setEditForm({...editForm, emergency_contact: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-200 rounded-xl font-body focus:ring-2 focus:ring-teal-500"
                    placeholder="Name and phone number"
                    data-testid="edit-child-emergency"
                  />
                </div>

                <div>
                  <label className="block font-body font-semibold text-slate-700 mb-1">Medical Conditions</label>
                  <textarea
                    value={editForm.medical_conditions}
                    onChange={(e) => setEditForm({...editForm, medical_conditions: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-200 rounded-xl font-body focus:ring-2 focus:ring-teal-500"
                    rows="2"
                    placeholder="Any allergies or conditions"
                    data-testid="edit-child-medical"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={saveChildEdit}
                    disabled={saving || !editForm.name}
                    className="flex-1 btn-primary flex items-center justify-center gap-2"
                    data-testid="save-child-edit"
                  >
                    {saving ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <>
                        <Save className="w-5 h-5" /> Save Changes
                      </>
                    )}
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="btn-outline"
                    data-testid="cancel-child-edit"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoolRegisterView;
